import { API_BASE_URL } from './index';
import { getToken, getUser } from './auth';

// Mock connectors data (removed - now using real API)
let mockConnectors = [
  {
    id: 1,
    name: 'CRM Integration',
    baseUrl: 'https://api.crm.example.com',
    authType: 'bearer',
    status: 'active',
    createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    headers: [
      { key: 'Authorization', value: 'Bearer {token}' },
      { key: 'Content-Type', value: 'application/json' },
    ],
    endpoints: [
      {
        id: 1,
        name: 'Get Customer',
        method: 'GET',
        path: '/customers/{id}',
        responseMapping: {
          customerName: '$.name',
          customerId: '$.id',
          email: '$.email',
        },
      },
      {
        id: 2,
        name: 'Create Ticket',
        method: 'POST',
        path: '/tickets',
        responseMapping: {
          ticketId: '$.id',
          status: '$.status',
        },
      },
    ],
  },
  {
    id: 2,
    name: 'Payment Gateway',
    baseUrl: 'https://api.payment.example.com',
    authType: 'api_key',
    status: 'inactive',
    createdAt: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    headers: [
      { key: 'X-API-Key', value: '{api_key}' },
    ],
    endpoints: [
      {
        id: 1,
        name: 'Process Payment',
        method: 'POST',
        path: '/payments',
        responseMapping: {
          transactionId: '$.transaction.id',
          status: '$.transaction.status',
        },
      },
    ],
  },
];

export const connectorsApi = {
  getConnectors: async () => {
    const token = getToken();
    const user = getUser();

    if (!token) {
      throw new Error('No authentication token');
    }
    if (!user?.companyId) {
      throw new Error('Company ID not available');
    }

    const response = await fetch(`${API_BASE_URL}/api/connectors`, {
      headers: {
        'Content-Type': 'application/json',
        'X-Session-Token': token,
        'X-Company-ID': user.companyId,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to load connectors' }));
      throw new Error(error.error || 'Failed to load connectors');
    }

    return response.json();
  },

  getConnector: async (id) => {
    const token = getToken();
    const user = getUser();

    if (!token) {
      throw new Error('No authentication token');
    }
    if (!user?.companyId) {
      throw new Error('Company ID not available');
    }

    const response = await fetch(`${API_BASE_URL}/api/connectors/${id}`, {
      headers: {
        'Content-Type': 'application/json',
        'X-Session-Token': token,
        'X-Company-ID': user.companyId,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to load connector' }));
      throw new Error(error.error || 'Failed to load connector');
    }

    return response.json();
  },

  createConnector: async (data) => {
    const token = getToken();
    const user = getUser();

    if (!token) {
      throw new Error('No authentication token');
    }
    if (!user?.companyId) {
      throw new Error('Company ID not available');
    }

    const response = await fetch(`${API_BASE_URL}/api/connectors`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Session-Token': token,
        'X-Company-ID': user.companyId,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to create connector' }));
      throw new Error(error.error || 'Failed to create connector');
    }

    return response.json();
  },

  updateConnector: async (id, data) => {
    const token = getToken();
    const user = getUser();

    if (!token) {
      throw new Error('No authentication token');
    }
    if (!user?.companyId) {
      throw new Error('Company ID not available');
    }

    const response = await fetch(`${API_BASE_URL}/api/connectors/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Session-Token': token,
        'X-Company-ID': user.companyId,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to update connector' }));
      throw new Error(error.error || 'Failed to update connector');
    }

    return response.json();
  },

  deleteConnector: async (id) => {
    const token = getToken();
    const user = getUser();

    if (!token) {
      throw new Error('No authentication token');
    }
    if (!user?.companyId) {
      throw new Error('Company ID not available');
    }

    const response = await fetch(`${API_BASE_URL}/api/connectors/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-Session-Token': token,
        'X-Company-ID': user.companyId,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to delete connector' }));
      throw new Error(error.error || 'Failed to delete connector');
    }

    return response.json();
  },

  testConnector: async (id, params = {}) => {
    const token = getToken();
    const user = getUser();

    if (!token) {
      throw new Error('No authentication token');
    }
    if (!user?.companyId) {
      throw new Error('Company ID not available');
    }

    const response = await fetch(`${API_BASE_URL}/api/connectors/${id}/test`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Session-Token': token,
        'X-Company-ID': user.companyId,
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Connector test failed' }));
      throw new Error(error.error || 'Connector test failed');
    }

    return response.json();
  },
};

